import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Calendar, Heart, Users, CheckCircle, Star } from 'lucide-react';
import { ageBooks } from '../data/mock';

const HomePage = () => {
  const featuredBooks = ageBooks.filter(book => book.age >= 2 && book.age <= 8);
  const [topReviews, setTopReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(true);

  useEffect(() => {
    const fetchTopReviews = async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${API_URL}/api/reviews`);
        if (response.ok) {
          const reviews = await response.json();
          // Sort by rating (highest first), then by date (newest first)
          const sorted = reviews
            .filter(r => r.status === 'approved' && r.rating >= 4)
            .sort((a, b) => {
              if (b.rating !== a.rating) return b.rating - a.rating;
              return new Date(b.created_at) - new Date(a.created_at);
            })
            .slice(0, 3);
          setTopReviews(sorted);
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
      } finally {
        setLoadingReviews(false);
      }
    };

    fetchTopReviews();
  }, []);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" style={{ minHeight: '70vh' }}>
            <div>
              <div className="hero-announcement">
                <Star size={16} />
                <span>Premium Parent-Guided Curriculum</span>
              </div>
              
              <h1 className="heading-hero mb-6">
                Emotional Intelligence Academy
              </h1>
              
              <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
                A premium, parent-guided emotional skills curriculum. One book per age. 40 weeks of scripts and practice that grow with your child.
              </p>
              
              <div className="hero-buttons">
                <Link to="/start-here" className="btn-primary">
                  Find Your Child's Book
                </Link>
              </div>
            </div>

            <div className="flex justify-center">
              <img 
                src="https://images.pexels.com/photos/7978486/pexels-photo-7978486.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Happy family bonding together in a sunny park"
                className="rounded-2xl shadow-lg w-full object-cover hover-lift"
                style={{ maxHeight: '450px' }}
                data-testid="hero-banner-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1 section-title">How It Works</h2>
            <p className="body-large section-subtitle">
              Simple, structured, and built for busy families
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="card text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(91, 127, 255, 0.15) 0%, rgba(167, 139, 250, 0.15) 100%)' }}>
                  <BookOpen size={32} style={{ color: 'var(--brand-primary)' }} />
                </div>
              </div>
              <h3 className="heading-3 mb-3">1. Choose Child's Age</h3>
              <p className="body-small">
                Start with your child's current age. Each book is specifically designed for that year of development.
              </p>
            </div>

            <div className="card text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(255, 169, 77, 0.15) 0%, rgba(255, 107, 157, 0.15) 100%)' }}>
                  <Calendar size={32} style={{ color: 'var(--brand-accent)' }} />
                </div>
              </div>
              <h3 className="heading-3 mb-3">2. Follow the Weekly Focus</h3>
              <p className="body-small">
                Each week introduces one skill target with clear guidance and parent scripts for real situations.
              </p>
            </div>

            <div className="card text-center">
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, rgba(122, 231, 199, 0.15) 0%, rgba(78, 205, 196, 0.15) 100%)' }}>
                  <Heart size={32} style={{ color: 'var(--brand-success)' }} />
                </div>
              </div>
              <h3 className="heading-3 mb-3">3. Practice a Little Each Day</h3>
              <p className="body-small">
                Short daily lessons (5-10 mins or less) with repeated practice so skills actually stick.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why This Is Different Section */}
      <section className="section" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="heading-1 mb-6">Why This Is Different</h2>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                This is not a workbook. It's a complete emotional development roadmap with scripts, structure, and age-specific progression.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={24} style={{ color: 'var(--brand-success)', flexShrink: 0 }} />
                  <div>
                    <h4 className="heading-3 mb-1">Parent Scripts Included</h4>
                    <p className="body-small">
                      Exact language to use during conflict, repair, boundaries, and big feelings. No guessing.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle size={24} style={{ color: 'var(--brand-success)', flexShrink: 0 }} />
                  <div>
                    <h4 className="heading-3 mb-1">40-Week Structured Sequence</h4>
                    <p className="body-small">
                      A clear progression that builds skill over time, not random activities.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle size={24} style={{ color: 'var(--brand-success)', flexShrink: 0 }} />
                  <div>
                    <h4 className="heading-3 mb-1">Age-Specific Development</h4>
                    <p className="body-small">
                      One book per age, built for that year's language, regulation, and social skills.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <CheckCircle size={24} style={{ color: 'var(--brand-success)', flexShrink: 0 }} />
                  <div>
                    <h4 className="heading-3 mb-1">Regulation Tools That Stick</h4>
                    <p className="body-small">
                      Practiced repeatedly so kids can actually use them during stress.
                    </p>
                  </div>
                </div>
              </div>

              <Link to="/series-overview" className="btn-primary mt-8 inline-block">
                See the Full Series
              </Link>
            </div>

            <div>
              <img 
                src="https://images.unsplash.com/photo-1758598737883-aee5195ae1da" 
                alt="Parent guiding child with learning activities"
                className="rounded-lg shadow-lg w-full hover-lift"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Featured Books Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1 section-title">Start with Your Child's Age</h2>
            <p className="body-large section-subtitle">
              Featured Ages 2-8 • <Link to="/start-here" className="text-brand-orange hover:underline">View all ages 2-12 →</Link>
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredBooks.map((book) => (
              <Link 
                key={book.age}
                to={`/book/${book.age}`}
                className={`age-card ${book.color}`}
                style={{ position: 'relative' }}
              >
                <div className="flex items-center justify-center w-16 h-16 rounded-full mb-4 mx-auto" style={{ background: 'rgba(255, 255, 255, 0.6)' }}>
                  <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    {book.age}
                  </span>
                </div>
                <h3 className="heading-3 text-center mb-2">{book.title}</h3>
                <p className="body-small text-center mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {book.focus}
                </p>
                <div className="mt-auto pt-4 border-t border-border-light">
                  <div className="flex flex-col gap-2">
                    <span className="btn-secondary text-xs py-2 px-4 text-center whitespace-nowrap">View Details</span>
                    {book.age <= 10 && (
                      <a 
                        href={book.amazonLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn-primary text-xs py-2 px-4 text-center whitespace-nowrap"
                        onClick={(e) => e.stopPropagation()}
                      >
                        Buy Now
                      </a>
                    )}
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="text-center mt-8">
            <Link to="/start-here" className="btn-primary">
              See All Ages (2-18)
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials Section - Only show if there are approved reviews */}
      {!loadingReviews && topReviews.length > 0 && (
        <section className="section" style={{ background: 'var(--bg-section)' }}>
          <div className="container">
            <div className="section-header">
              <h2 className="heading-1 section-title">What Parents Are Saying</h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {topReviews.map((review) => (
                <div key={review.id} className="card">
                  <div className="flex gap-1 mb-4">
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} size={16} fill="var(--brand-yellow)" color="var(--brand-yellow)" />
                    ))}
                  </div>
                  <h4 className="body-medium font-semibold mb-2">{review.title}</h4>
                  <p className="body-medium mb-4" style={{ fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                    "{review.content.length > 150 ? review.content.substring(0, 150) + '...' : review.content}"
                  </p>
                  <div className="border-t border-border-light pt-4">
                    <p className="body-small font-medium">{review.reviewer_name || 'Anonymous'}</p>
                    <p className="caption">Age {review.age} Curriculum</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* About Author Section */}
      <section className="section" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <Users size={48} className="mx-auto mb-6" style={{ color: 'var(--brand-primary)' }} />
            <h2 className="heading-1 mb-4">Built for Real Families</h2>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              This curriculum exists because most of us were never taught these skills ourselves. 
              We want something structured, realistic, and proven—not more guesswork.
            </p>
            <Link to="/about" className="btn-secondary">
              Learn More About Our Mission
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="section" style={{ background: 'var(--gradient-hero-subtle)' }}>
        <div className="container">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="heading-1 mb-4">Ready to Get Started?</h2>
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Start at your child's age today. Ten minutes a day can change the next ten years.
            </p>
            <Link to="/start-here" className="btn-primary">
              Find Your Child's Book
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
